import { AlertTriangle, ShieldCheck, Lightbulb } from 'lucide-react';

export const alcoolArticle = {
  slug: 'alcool-stupefiants-moto',
  weekVolume: 21,
  weekYear: 2026,
  monthLabel: 'Juin 2026',
  category: 'Prévention',
  shortTitle: 'Alcool & stupéfiants',
  title: 'Zéro alcool, zéro stupéfiant',
  kicker: 'Code de la route — Articles R234 & L235',
  subtitle: "Alcoolémie au guidon : un motard sur quatre tué présente un taux d'alcool supérieur au maximum légal. La conduite sous stupéfiants double le risque mortel.",
  heroImage: '/images/articles/enquete-05-alcool.png',
  heroImageAlt: "Casque de moto, clés et verre de whisky posés sur un comptoir en bois — mise en scène du dilemme alcool au guidon",
  coverPoster: '/images/articles/poster-05-alcool-horizontal.png',
  coverPosterAlt: "Poster CODE 2 ROUES — Dossier Enquête #5 : Alcool, 5 étapes (altère les réflexes, affecte l'équilibre, réduit la vision, sanctions lourdes, zéro alcool = zéro risque)",
  posterHorizontal: '/images/articles/poster-05-alcool-horizontal.png',
  posterVertical: '/images/articles/poster-05-alcool.png',
  posterVerticalAlt: "Poster vertical CODE 2 ROUES — Dossier Enquête #5 : Alcool, 5 étapes pour comprendre et faire le bon choix (altère les réflexes, affecte l'équilibre, réduit la vision, sanctions lourdes, zéro alcool = zéro risque)",
  topRightKicker: { line1: 'Conseil ECSR', line2: 'Prévention' },
  readTimeMin: 4,
  seoDescription: "Alcoolémie permis probatoire 0,2 g/L vs permis confirmé 0,5 g/L. Sanctions stupéfiants au guidon. Le guide ECSR 2026 pour motards.",
  seoKeywords: "alcool moto, stupéfiants moto, alcoolémie permis probatoire, R234, L235, dépistage stupéfiants, sanctions alcool guidon",
  about: 'Alcoolémie et stupéfiants au volant, prévention motards',
  source: { label: 'Sécurité routière', url: 'https://www.securite-routiere.gouv.fr', refs: 'Articles R234-1 à R234-7 et L235-1 du Code de la route.' },
  chapo: {
    kicker: 'Le constat',
    body: "L'alcool est impliqué dans <strong>30 % des accidents mortels</strong> de motards. Les stupéfiants représentent <strong>23 %</strong>. Combinés, l'alcool et les stupéfiants <strong>multiplient par 14</strong> le risque d'accident mortel.",
    sideStat: {
      kicker: 'Permis probatoire',
      big: '0,2 g/L',
      body: "Seuil légal pendant la <strong>période probatoire</strong> (3 ans pour le permis classique, 2 ans après formation AAC). Soit <strong>0,1 mg/L d'air expiré</strong>.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "À 0,5 g/L d'alcool dans le sang, le temps de réaction d'un motard augmente de 50 %, et son champ visuel se rétrécit comme dans un tunnel. À moto, c'est mortel.",
    source: 'Sécurité routière — Campagne 2025',
  },
  sections: [
    {
      part: 'Partie 1 — Les seuils',
      title: 'Quels taux légaux en 2026 ?',
      compareCards: [
        {
          accent: '#cf2031', kicker: 'Permis probatoire', title: '0,2 g/L sang',
          intro: 'Tolérance zéro pour les jeunes permis (3 ans normal, 2 ans AAC).',
          points: [
            { ok: false, text: "<strong>1 verre = dépassement</strong> dans la majorité des cas" },
            { ok: false, text: "Au-dessus : <strong>retrait 6 points</strong> + amende 135 €" },
            { ok: false, text: "Suspension du permis jusqu'à 3 ans + invalidation possible" },
            { ok: false, text: 'Stage de sensibilisation à payer (200 à 250 €)' },
          ],
        },
        {
          accent: '#16a34a', kicker: 'Permis confirmé', title: '0,5 g/L sang',
          intro: 'Seuil contraventionnel — au-dessus, sanctions immédiates.',
          points: [
            { ok: false, text: "De 0,5 à 0,8 g/L : <strong>contravention</strong> 4ᵉ classe (135 €) + 6 points" },
            { ok: false, text: "À partir de 0,8 g/L : <strong>délit</strong> — comparution + 4500 €" },
            { ok: false, text: 'Suspension automatique 3 ans + invalidation possible' },
            { ok: false, text: 'Travail d\'intérêt général ou prison ferme possibles' },
          ],
          footer: "Récidive = doublement automatique des peines + obligation de stage.",
        },
      ],
    },
    {
      part: 'Partie 2 — Les stupéfiants',
      title: "L'article L235-1 du Code de la route",
      paragraphs: [
        "Conduire un véhicule à moteur après avoir <strong>fait usage de substances classées comme stupéfiants</strong> (cannabis, cocaïne, amphétamines, opiacés, MDMA…) est un <strong>délit immédiat</strong>, quel que soit le seuil détecté.",
      ],
      cards: [
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Sanctions', title: 'Délit immédiat', body: "<strong>2 ans de prison</strong> + <strong>4500 € d'amende</strong> + retrait de <strong>6 points</strong> + suspension/invalidation du permis (max 3 ans)." },
        { icon: ShieldCheck, accent: '#1d4ed8', kicker: 'Dépistage', title: 'Test salivaire systématique', body: "Sur réquisition, accident corporel, contrôle aléatoire ou suspicion. <strong>Refus = mêmes sanctions</strong> que test positif." },
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Cumul', title: 'Alcool + stupéfiants', body: "<strong>3 ans de prison</strong> + <strong>9000 € d'amende</strong>. Les peines se cumulent. Récidive = peines doublées." },
        { icon: AlertTriangle, accent: '#7f1d1d', kicker: 'Délit + accident', title: "Si blessure ou décès", body: "<strong>Jusqu'à 10 ans de prison</strong> + <strong>150 000 €</strong> en cas d'accident mortel sous emprise." },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 3 — Le piège du lendemain',
      title: "L'élimination de l'alcool : 0,15 g/L par heure",
      paragraphs: [
        "Le <strong>foie élimine 0,1 à 0,15 g/L par heure</strong>. Aucune méthode (café, douche froide, sport) <strong>n'accélère</strong> ce processus. Conséquence : après une soirée, l'alcoolémie peut rester <strong>positive le lendemain matin</strong>.",
      ],
      callouts: [
        { icon: Lightbulb, accent: '#f59e0b', body: "<strong>Exemple :</strong> 4 verres d'alcool standard à minuit (≈ 0,8 g/L) → encore <strong>0,4-0,5 g/L à 7h du matin</strong>. Tu prends ton permis probatoire pour aller au boulot ? Tu es au-dessus du seuil." },
        { icon: ShieldCheck, accent: '#16a34a', body: "<strong>Bonne pratique :</strong> investis 5 € dans un éthylotest chimique (durée de vie 2 ans). Vérifie avant de prendre la moto. La <strong>seule méthode fiable</strong>." },
      ],
    },
  ],
  quiz: [
    { id: 'q1', question: 'Quel est le taux maximum légal en permis probatoire ?', options: ['0,5 g/L', '0,2 g/L', '0,8 g/L', "0 g/L (zéro absolu)"], correctIndex: 1, explain: "0,2 g/L pendant la période probatoire (3 ans permis classique, 2 ans après AAC). Soit 0,1 mg/L d'air expiré." },
    { id: 'q2', question: "Combien de points sont retirés pour conduite avec 0,3 g/L d'alcool en permis probatoire ?", options: ['3 points', '6 points', '4 points', 'Aucun, juste une amende'], correctIndex: 1, explain: "Au-dessus de 0,2 g/L en probatoire = 6 points retirés + 135 € + suspension immédiate possible." },
    { id: 'q3', question: 'À partir de quel taux la conduite devient-elle un DÉLIT (et non plus contravention) ?', options: ['0,3 g/L', '0,5 g/L', '0,8 g/L', '1,0 g/L'], correctIndex: 2, explain: "À partir de 0,8 g/L (sang) ou 0,4 mg/L (air) : délit pénal. Comparution, 4500 € amende, 2 ans prison max, suspension." },
    { id: 'q4', question: 'Quelle vitesse le foie élimine-t-il l\'alcool ?', options: ['0,5 g/L par heure', '0,3 g/L par heure', '0,1 à 0,15 g/L par heure', 'Variable selon la personne, impossible à dire'], correctIndex: 2, explain: "0,1 à 0,15 g/L par heure. Aucune méthode n'accélère ce processus. Café, sport, douche : sans effet." },
    { id: 'q5', question: 'Que risque-t-on en cas de refus de test salivaire (stupéfiants) ?', options: ['Rien, on a le droit', 'Une amende seulement', 'Les mêmes sanctions que test positif', "Test obligatoire à la prise de sang"], correctIndex: 2, explain: "Le refus est puni des mêmes peines que le résultat positif : 2 ans prison + 4500 € + 6 points + suspension." },
  ],
  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: 'Sanctions complètes & barèmes',
    body: 'Toutes les amendes, retraits de points et peines associées aux infractions du Code de la route en 2026.',
    primary: { label: 'Voir le barème complet', to: '/sanctions-baremes' },
    secondary: { label: 'Lexique alcool & stupéfiants', to: '/lexique?q=alcool' },
  },
};
